import React from 'react';
import { connect } from 'react-redux';

// Actions
import { LOAD_COMPARISON, SET_IS_RERUNNING } from '@/redux/modules/comparison';
import { selectIsLoadingSome } from '@/redux/modules/ui/loaders';

// Components
import PortfolioEditorContent from '@/views/components/modals/portfolio-editor-builder/components/PortfolioEditorContent/PortfolioEditorContent';
import { CloseButton, PrimaryButton } from '@/views/components/buttons';
import { FormattedMessageV2 } from '@/views/components/i18n';
import Modal from '@/views/components/modals/AccessibleModal';
import { Typography } from '@capintel/component-lib/typography';

// Types
import Locale from '@/types/locale';
import Portfolio from '@/types/portfolio';
import { ReduxState } from '@/redux/modules/types';

// Utils
import { isEnterpriseModel } from '@/utils/portfolio/utils';
import { formatMessage } from '@/utils/i18n';
import { getTotalPortfolioAllocation } from '@/utils/portfolios';
import { ALLOWED_HTML_TAGS } from '../../i18n/FormattedMessageV2';

type StateProps = {
  isRerunning: boolean;
  locale: Locale;
  portfolio: Portfolio;
};

type Props = StateProps & {
  show: boolean;
  onClose: () => void;
  onRerunCurrentComparison: () => void;
};

const PortfolioReadOnlyModal = (props: Props) => {
  const { isRerunning, locale, portfolio, show, onClose, onRerunCurrentComparison } = props;

  // Variables Declaration
  const i18nModalBodyPath = 'Modal.PortfolioReadOnly.Body';
  const i18nModalFooterPath = 'Modal.PortfolioReadOnly.Footer.Actions';
  const totalAllocated = getTotalPortfolioAllocation(portfolio);

  // MODAL HEADER
  const renderHeader = (): JSX.Element => (
    <Modal.Header>
      {/* Provisional fix. Pending migration to library modal. */}
      <div className="modal-title">
        <Typography as={ALLOWED_HTML_TAGS.H2} color="primary" variant="headerXs">
          <FormattedMessageV2 toLocalize={portfolio.name} />
        </Typography>
      </div>
      <CloseButton onClick={onClose} />
    </Modal.Header>
  );

  // MODAL BODY
  const renderBody = (): JSX.Element => {
    const { discretionaryMer, riskProfile, size } = portfolio;

    const portfolioAdvisoryFee = discretionaryMer ? Number((discretionaryMer * 100).toFixed(4)) : 0;
    const portfolioRiskProfile = riskProfile?.profile.title[locale];
    const portfolioRoundedSize = size ? +size.toFixed(2) : undefined;

    return (
      <Modal.Body>
        <div className="header-portfolio-details">
          <div className="header-portfolio-details-item">
            <FormattedMessageV2 className="label" toLocalize={`${i18nModalBodyPath}.TotalValueLabel`} />
            <div className="value">{`$ ${portfolioRoundedSize}`}</div>
          </div>
          <div className="header-portfolio-details-item">
            <FormattedMessageV2 className="label" toLocalize={`${i18nModalBodyPath}.AdvisoryFeeLabel`} />
            <div className="value">{`${portfolioAdvisoryFee} ${formatMessage(`${i18nModalBodyPath}.FeeUnit`)}`}</div>
          </div>
          <div className="header-portfolio-details-item">
            <FormattedMessageV2 className="label" toLocalize={`${i18nModalBodyPath}.RiskProfileLabel`} />
            <div className="value">{portfolioRiskProfile || <div className="empty-value" />}</div>
          </div>
        </div>
        <PortfolioEditorContent totalAllocated={totalAllocated} />
      </Modal.Body>
    );
  };

  // MODAL FOOTER
  const renderFooter = (): JSX.Element => {
    const showModifyPortfolioSection = !isEnterpriseModel(portfolio) && portfolio.availableActions?.edit;

    return (
      <Modal.Footer>
        {showModifyPortfolioSection && (
          <div className="primary-action-container">
            <FormattedMessageV2 className="modify-portfolio-description" icon="ds-warning" toLocalize={`${i18nModalFooterPath}.ModifyPortfolio.Description`} />
            <PrimaryButton className="primary-v2" loading={isRerunning} onClick={onRerunCurrentComparison}>
              {formatMessage(`${i18nModalFooterPath}.ModifyPortfolio.Label`)}
            </PrimaryButton>
          </div>
        )}
      </Modal.Footer>
    );
  };

  return (
    <Modal backdropClassName="modal-v2-backdrop" className="modal-v2 portfolio-read-only-modal" show={show} onHide={onClose}>
      {renderHeader()}
      {renderBody()}
      {renderFooter()}
    </Modal>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  isRerunning: selectIsLoadingSome(state, [SET_IS_RERUNNING.type, LOAD_COMPARISON.type]),
  locale: state.intl.locale,
  portfolio: state.editPortfolio.portfolio,
});

export default connect(mapStateToProps)(PortfolioReadOnlyModal);
